import {
  useRef,
  useState,
  type ComponentPropsWithRef,
  type MouseEvent,
} from "react";
import { usePreventScroll } from "react-aria";
import { type PropsWithOptionalTestID } from "@boi/components-core";
import { FocusScope } from "@react-aria/focus";

import { OverlayProvider } from "../OverlayContext";

import * as S from "./Modal.styled";
import {
  useSyncDialogAttributes,
  useKeyboardEventListener,
} from "./Modal.hooks";

export type ModalProps = PropsWithOptionalTestID &
  Omit<ComponentPropsWithRef<"dialog">, "onClose" | "onKeyDown" | "open"> & {
    isOpen?: boolean;
    onRequestClose?: () => void;
    tabletWidth?: number;
  };

function Modal(props: ModalProps) {
  const {
    isOpen,
    onRequestClose,
    children,
    testID = "modal",
    tabletWidth,
    ...dialogProps
  } = props;

  const ref = useRef<HTMLDialogElement>(null);

  /**
   * Note: the OverlayProvider intentionally takes the DOM node, not refs, in order to be able to update when the nodes change.
   * A callback ref is used here to permit this behaviour, and useState is an appropriate way to implement this.
   */
  const [portalElement, setPortalElement] = useState<HTMLDivElement>();

  function setPortalRef(element: HTMLDivElement | null) {
    if (element) {
      setPortalElement(element);
    }
  }

  useSyncDialogAttributes(ref, isOpen);
  useKeyboardEventListener(ref, onRequestClose);

  /**
   * Stop propagation when clicking on the content container so it
   * doesn't trigger the onClick at the dialog element, which closes the Modal
   */
  const containerClickHandler = (evt: MouseEvent<HTMLDivElement>) => {
    evt.stopPropagation();
  };

  usePreventScroll({ isDisabled: !isOpen });

  return (
    <OverlayProvider portalElement={portalElement}>
      <FocusScope contain={isOpen} restoreFocus>
        <S.Dialog
          {...dialogProps}
          ref={ref}
          aria-modal={true}
          data-testid={testID}
          onClick={onRequestClose}
        >
          <S.Container
            onClick={containerClickHandler}
            $tabletWidth={tabletWidth}
          >
            {children}
            <div ref={setPortalRef} />
          </S.Container>
        </S.Dialog>
      </FocusScope>
    </OverlayProvider>
  );
}

export default Modal;
